// import mixpanel from "mixpanel-browser";
// import { getMixpanelProjectToken, isDev } from "../../src/utils/config";
import { isDev } from "../../src/utils/config";
import { log } from "../../src/utils/log";
import { trackTelemetryFromDobbyBackend } from "./telemetryApi";

export class EventLogger {
  private static instance: EventLogger;

  private userId?: string;

  // private initialized: Promise<void>;

  private constructor() {
    // Mixpanel browser SDK initialization is now handled by the backend.
    // this.initialized = this.initMixpanelClient();
  }

  public static getInstance() {
    if (!EventLogger.instance) {
      EventLogger.instance = new EventLogger();
    }
    return EventLogger.instance;
  }

  // Mixpanel browser SDK initialization commented out — centralized on backend.
  // initMixpanelClient = async () => { ... };

  setUser = (userId: string): void => {
    if (this.userId !== userId) {
      log.debug(`[eventLogger] setting user ${userId}`);
      this.userId = userId;
    }
  };

  track = (originalEvent: ClientEvent) => {
    const event: any = {
      environment: isDev() ? "dev" : "prod",
      ...originalEvent,
    };
    delete event.name;

    if (this.userId) {
      event.distinct_id = this.userId;
    }

    log.debug(`[eventLogger] log event ${JSON.stringify(originalEvent)}`);

    // Mixpanel and New Relic are now handled by the backend via /telemetry/log.
    // The backend has full auth context (userId, email, orgId, orgName, client).

    // PostgreSQL + Mixpanel + New Relic telemetry via backend
    trackTelemetryFromDobbyBackend({
      name: originalEvent.name,
      extra: event,
    });
  };

  // timeEvent commented out — Mixpanel/New Relic now handled by backend.
  // timeEvent = (eventName: string) => { ... };
}

interface Event {
  name: string;
  extra?: any;
}

interface ClickEvent extends Event {
  name: "click";
  action:
    | "open_shipment"
    | "open_mail"
    | "download"
    | "other"
    | "download_attachment";
  rowId?: string;
  attachmentName?: string;
  shipmentId?: string;
  messageId?: string;
  threadId?: string;
}

interface PostFeedbackEvent extends Event {
  name: "post_feedback";
  category: "accessorial_charges" | "generic";
}

interface SearchEvent extends Event {
  name: "search";
  action: "ajww_search_rate" | "ajww_search_quote" | "shipment_search";
  query: string;
}

interface ChatConversationStartedEvent extends Event {
  name: "chat_conversation_started";
  conversationId: string;
  conversationType: string;
  feedId?: string;
  hasInitialMessage: boolean;
}

interface ChatMessageSentEvent extends Event {
  name: "chat_message_sent";
  conversationId: string;
  message: string;
  conversationType: string;
  feedId?: string;
}

interface ChatResponseReceivedEvent extends Event {
  name: "chat_response_received";
  conversationId: string;
  conversationType: string;
  feedId?: string;
  hasResponse: boolean;
  sourcesCount: number;
}

interface ShipmentTimelineSearchEvent extends Event {
  name: "shipment_timeline_search";
  query: string;
}

interface OutlookPluginOpenProductShipmentTimelineEvent extends Event {
  name: "outlook_plugin_open_product_shipment_timeline";
  messageId: string | null;
  shipmentFound: boolean;
  shipmentCount: number;
  error: string | null;
}

interface ShipmentTimelineOpenEvent extends Event {
  name: "shipment_timeline_open";
  shipmentId: string;
}

interface ShipmentTimelineActionDismissedEvent extends Event {
  name: "shipment_timeline_action_dismissed";
  actionId: string;
  actionType: string;
  reminderId?: string;
  reminderType?: string;
  shipmentId?: string;
}

interface ShipmentTimelineActionTriggeredEvent extends Event {
  name: "shipment_timeline_action_triggered";
  actionId: string;
  actionType: string;
  reminderId?: string;
  reminderType?: string;
  reminderName?: string;
  shipmentId?: string;
}

interface ShipmentTimelineActionEmailSentEvent extends Event {
  name: "shipment_timeline_action_email_sent";
  actionId: string;
  actionType: string;
  reminderId?: string;
  reminderType?: string;
  reminderName?: string;
  shipmentId?: string;
  emailTo?: string[];
  emailCc?: string[];
  emailSubject?: string;
  emailBody?: string;
}

type ClientEvent =
  | ClickEvent
  | SearchEvent
  | PostFeedbackEvent
  | ChatConversationStartedEvent
  | ChatMessageSentEvent
  | ChatResponseReceivedEvent
  | OutlookPluginOpenProductShipmentTimelineEvent
  | ShipmentTimelineSearchEvent
  | ShipmentTimelineOpenEvent
  | ShipmentTimelineActionDismissedEvent
  | ShipmentTimelineActionTriggeredEvent
  | ShipmentTimelineActionEmailSentEvent;

export const eventLogger = EventLogger.getInstance();
